import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { CalendarIcon, Users, ArrowRightLeft, Search } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

export function SearchForm({ compact = false }) {
  const navigate = useNavigate();
  const [ports, setPorts] = useState([]);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [date, setDate] = useState(new Date());
  const [pax, setPax] = useState("1");

  useEffect(() => {
    api.get("/ports").then((r) => setPorts(r.data)).catch(() => {});
  }, []);

  const swap = () => {
    setFrom(to);
    setTo(from);
  };

  const submit = () => {
    if (!from || !to) return toast.error("Please select both departure and arrival ports");
    if (from === to) return toast.error("Departure and arrival ports must differ");
    const d = format(date, "yyyy-MM-dd");
    navigate(`/search?from=${from}&to=${to}&date=${d}&pax=${pax}`);
  };

  return (
    <div
      className={`glass rounded-xl p-4 sm:p-5 shadow-lg ${compact ? "" : "shadow-2xl"}`}
      data-testid="search-form"
    >
      <div className="grid gap-3 md:grid-cols-[1fr_auto_1fr_1fr_auto_auto] md:items-end">
        <PortField label="From" value={from} onChange={setFrom} ports={ports} testid="from-port-select" />
        <div className="hidden md:flex items-end pb-1.5">
          <Button variant="outline" size="icon" onClick={swap} className="rounded-full h-9 w-9" data-testid="swap-ports-btn" aria-label="Swap ports">
            <ArrowRightLeft className="h-4 w-4" />
          </Button>
        </div>
        <PortField label="To" value={to} onChange={setTo} ports={ports} testid="to-port-select" />

        <div className="flex flex-col gap-1.5">
          <label className="overline text-muted-foreground">Date</label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="justify-start font-normal bg-white h-11" data-testid="date-picker-btn">
                <CalendarIcon className="mr-2 h-4 w-4 text-accent" />
                {format(date, "dd MMM yyyy")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar mode="single" selected={date} onSelect={(d) => d && setDate(d)} disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))} initialFocus />
            </PopoverContent>
          </Popover>
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="overline text-muted-foreground">Guests</label>
          <Select value={pax} onValueChange={setPax}>
            <SelectTrigger className="bg-white h-11 w-full md:w-24" data-testid="pax-select">
              <Users className="h-4 w-4 text-accent mr-1" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6].map((n) => (
                <SelectItem key={n} value={String(n)}>{n}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button onClick={submit} className="bg-accent hover:bg-accent/90 text-white h-11 px-6 font-semibold" data-testid="search-submit-btn">
          <Search className="h-4 w-4 mr-2" /> Search
        </Button>
      </div>
    </div>
  );
}

function PortField({ label, value, onChange, ports, testid }) {
  return (
    <div className="flex flex-col gap-1.5">
      <label className="overline text-muted-foreground">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-white h-11" data-testid={testid}>
          <SelectValue placeholder="Select port" />
        </SelectTrigger>
        <SelectContent>
          {ports.map((p) => (
            <SelectItem key={p.id} value={p.id}>
              {p.name} · {p.city}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
